import type { ApiErrorCode } from './types';

const STATUS_TO_CODE: Record<number, ApiErrorCode> = {
  400: 'ValidationError',
  401: 'Unauthorized',
  403: 'Forbidden',
  402: 'PaymentRequired',
  404: 'NotFound',
  409: 'Conflict',
  429: 'RateLimited',
  500: 'InternalError',
};

/** Thrown by every method on `CaptureApiClient`. Carries the platform's
 * shared error envelope (INTERFACES.md section 3) so a caller can branch
 * on `.code` instead of parsing `.message`. */
export class ApiError extends Error {
  readonly code: ApiErrorCode;
  readonly status: number;

  constructor(code: ApiErrorCode, status: number, message: string) {
    super(message);
    this.name = 'ApiError';
    this.code = code;
    this.status = status;
  }

  /** Builds an `ApiError` from a fetch `Response` that returned a non-2xx
   * status, reading the platform's `{ error, message }` envelope when the
   * server sent one and falling back to a generic message when it did not
   * (a gateway timeout or a 5xx with no body, for example). */
  static async fromResponse(response: Response): Promise<ApiError> {
    const fallbackCode = STATUS_TO_CODE[response.status] ?? 'InternalError';
    let body: unknown;
    try {
      body = await response.json();
    } catch {
      body = undefined;
    }
    if (
      body &&
      typeof body === 'object' &&
      'error' in body &&
      'message' in body &&
      typeof (body as { error: unknown }).error === 'string' &&
      typeof (body as { message: unknown }).message === 'string'
    ) {
      const envelope = body as { error: string; message: string };
      return new ApiError(envelope.error as ApiErrorCode, response.status, envelope.message);
    }
    return new ApiError(fallbackCode, response.status, `Request failed with status ${response.status}`);
  }

  /** True for the 402 that `POST /capture` and `POST /capture/upload-url`
   * answer when the tenant has no credits to start a capture. Retrying
   * cannot change the answer until credits are added, so callers stop
   * retrying and show `.message`, which the API words for the operator. */
  get isPaymentRequired(): boolean {
    return this.status === 402;
  }

  /** True for the 409 `POST /capture` answers when the same photo is already a
   * capture of the job (its message begins "Already in this job"). */
  get isDuplicateInJob(): boolean {
    return this.status === 409 && this.message.startsWith('Already in this job');
  }

  /** For network failures that never reach the server: offline, DNS,
   * CORS. There is no envelope to read, so the caller still gets a typed
   * error rather than a raw `TypeError` from `fetch`. */
  static network(message: string): ApiError {
    return new ApiError('InternalError', 0, message);
  }
}
